import { useMemo, useState } from "react";
import ExcelJS from "exceljs";
import { FiX, FiDownload, FiCalendar, FiBarChart2, FiHash, FiTrendingUp } from "react-icons/fi";
import { getRevenueReport } from "../actions/studensts";
import Loading from "../components/Loading";

const CURRENCY_FORMAT = '"$"#,##0.00';

function formatMoney(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);
}

function formatDisplayDate(value) {
  if (!value) return "-";
  return new Date(`${value}T00:00:00`).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function triggerDownload(buffer, fileName) {
  const blob = new Blob(
    [buffer],
    {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }
  );
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function applyCellBorder(cell) {
  cell.border = {
    top: { style: "thin", color: { argb: "FFE2E8F0" } },
    left: { style: "thin", color: { argb: "FFE2E8F0" } },
    bottom: { style: "thin", color: { argb: "FFE2E8F0" } },
    right: { style: "thin", color: { argb: "FFE2E8F0" } },
  };
}

async function buildRevenueWorkbook(rows, summary) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Qatar Authentication Admin";
  workbook.company = "Qatar Authentication";
  workbook.subject = "Revenue Report";
  workbook.title = `Revenue Report ${summary.start_date} to ${summary.end_date}`;
  workbook.created = new Date();
  workbook.modified = new Date();

  const summarySheet = workbook.addWorksheet("Summary", {
    views: [{ showGridLines: false }],
  });

  summarySheet.columns = [
    { width: 18 },
    { width: 18 },
    { width: 18 },
    { width: 18 },
    { width: 18 },
    { width: 18 },
  ];

  summarySheet.mergeCells("A1:F1");
  summarySheet.getCell("A1").value = "Admin Revenue Report";
  summarySheet.getCell("A1").font = { size: 20, bold: true, color: { argb: "FFFFFFFF" } };
  summarySheet.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };
  summarySheet.getCell("A1").fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF0F172A" },
  };
  summarySheet.getRow(1).height = 28;

  summarySheet.mergeCells("A2:F2");
  summarySheet.getCell("A2").value = `Reporting window: ${formatDisplayDate(summary.start_date)} to ${formatDisplayDate(summary.end_date)}`;
  summarySheet.getCell("A2").font = { size: 11, color: { argb: "FFCBD5E1" } };
  summarySheet.getCell("A2").alignment = { horizontal: "center" };
  summarySheet.getCell("A2").fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF1E293B" },
  };

  const summaryCards = [
    {
      range: "A4:B6",
      label: "Date Range",
      value: `${formatDisplayDate(summary.start_date)} - ${formatDisplayDate(summary.end_date)}`,
      fill: "FFF59E0B",
    },
    {
      range: "C4:D6",
      label: "Total Revenue",
      value: formatMoney(summary.total_revenue),
      fill: "FF059669",
    },
    {
      range: "E4:F6",
      label: "Invoices",
      value: Number(summary.total_invoices) || 0,
      fill: "FF2563EB",
    },
  ];

  summaryCards.forEach(({ range, label, value, fill }) => {
    summarySheet.mergeCells(range);
    const cell = summarySheet.getCell(range.split(":")[0]);
    cell.value = `${label}\n${value}`;
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.font = { bold: true, size: 14, color: { argb: "FFFFFFFF" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fill } };
    applyCellBorder(cell);
  });

  summarySheet.getCell("A8").value = "Report Highlights";
  summarySheet.getCell("A8").font = { bold: true, size: 13, color: { argb: "FF0F172A" } };

  const averageRevenue = rows.length > 0 ? Number(summary.total_revenue) / rows.length : 0;
  const highestDay = rows.reduce(
    (best, row) => (Number(row.daily_total) > Number(best.daily_total || 0) ? row : best),
    {}
  );

  const highlightRows = [
    ["Generated On", new Date().toLocaleString("en-GB")],
    ["Daily Entries", rows.length],
    ["Average Daily Revenue", averageRevenue],
    ["Best Revenue Day", highestDay.invoice_date ? formatDisplayDate(highestDay.invoice_date) : "No entries"],
    ["Best Day Total", highestDay.daily_total ? Number(highestDay.daily_total) : 0],
  ];

  highlightRows.forEach((item, index) => {
    const rowNumber = 9 + index;
    summarySheet.getCell(`A${rowNumber}`).value = item[0];
    summarySheet.getCell(`A${rowNumber}`).font = { bold: true, color: { argb: "FF334155" } };
    summarySheet.getCell(`B${rowNumber}`).value = item[1];
    summarySheet.getCell(`B${rowNumber}`).alignment = { horizontal: "left" };
    [summarySheet.getCell(`A${rowNumber}`), summarySheet.getCell(`B${rowNumber}`)].forEach((cell) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: rowNumber % 2 === 0 ? "FFF8FAFC" : "FFFFFFFF" } };
      applyCellBorder(cell);
    });
  });

  summarySheet.getCell("B11").numFmt = CURRENCY_FORMAT;
  summarySheet.getCell("B13").numFmt = CURRENCY_FORMAT;

  const detailsSheet = workbook.addWorksheet("Daily Ledger", {
    views: [{ state: "frozen", ySplit: 4 }],
  });

  detailsSheet.columns = [
    { header: "Date", key: "invoice_date", width: 16 },
    { header: "Main Revenue", key: "daily_main", width: 18 },
    { header: "Another Invoice Revenue", key: "daily_another", width: 24 },
    { header: "Daily Total", key: "daily_total", width: 18 },
    { header: "Invoice Count", key: "invoice_count", width: 16 },
  ];

  detailsSheet.mergeCells("A1:E1");
  detailsSheet.getCell("A1").value = "Revenue Ledger";
  detailsSheet.getCell("A1").font = { size: 18, bold: true, color: { argb: "FFFFFFFF" } };
  detailsSheet.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };
  detailsSheet.getCell("A1").fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF0F172A" },
  };

  detailsSheet.mergeCells("A2:E2");
  detailsSheet.getCell("A2").value = `${formatDisplayDate(summary.start_date)} to ${formatDisplayDate(summary.end_date)}`;
  detailsSheet.getCell("A2").font = { size: 10, color: { argb: "FFCBD5E1" } };
  detailsSheet.getCell("A2").alignment = { horizontal: "center" };
  detailsSheet.getCell("A2").fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF1E293B" },
  };

  const headerRow = detailsSheet.getRow(4);
  headerRow.values = ["Date", "Main Revenue", "Another Invoice Revenue", "Daily Total", "Invoice Count"];
  headerRow.height = 22;
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0F766E" } };
    applyCellBorder(cell);
  });

  if (rows.length === 0) {
    const emptyRow = detailsSheet.addRow(["No revenue entries found for this date range.", "", "", "", ""]);
    detailsSheet.mergeCells(`A5:E5`);
    detailsSheet.getCell("A5").alignment = { horizontal: "center", vertical: "middle" };
    detailsSheet.getCell("A5").font = { italic: true, color: { argb: "FF64748B" } };
  } else {
    rows.forEach((row, index) => {
      const excelRow = detailsSheet.addRow({
        invoice_date: formatDisplayDate(row.invoice_date),
        daily_main: Number(row.daily_main),
        daily_another: Number(row.daily_another),
        daily_total: Number(row.daily_total),
        invoice_count: Number(row.invoice_count),
      });
      excelRow.height = 20;
      excelRow.eachCell((cell, colNumber) => {
        cell.alignment = { horizontal: colNumber === 1 ? "left" : "center", vertical: "middle" };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: index % 2 === 0 ? "FFF8FAFC" : "FFFFFFFF" },
        };
        applyCellBorder(cell);
      });
      excelRow.getCell(2).numFmt = CURRENCY_FORMAT;
      excelRow.getCell(3).numFmt = CURRENCY_FORMAT;
      excelRow.getCell(4).numFmt = CURRENCY_FORMAT;
    });
  }

  const totalRow = detailsSheet.addRow([
    "TOTAL",
    rows.reduce((sum, row) => sum + Number(row.daily_main), 0),
    rows.reduce((sum, row) => sum + Number(row.daily_another), 0),
    Number(summary.total_revenue),
    Number(summary.total_invoices),
  ]);
  totalRow.height = 22;
  totalRow.eachCell((cell, colNumber) => {
    cell.font = { bold: true, color: { argb: colNumber === 1 ? "FF0F172A" : "FF111827" } };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFDE68A" } };
    applyCellBorder(cell);
  });
  totalRow.getCell(2).numFmt = CURRENCY_FORMAT;
  totalRow.getCell(3).numFmt = CURRENCY_FORMAT;
  totalRow.getCell(4).numFmt = CURRENCY_FORMAT;

  detailsSheet.autoFilter = "A4:E4";

  return workbook.xlsx.writeBuffer();
}

export default function RevenueExportModal({ onClose }) {
  const today = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const reportDays = useMemo(() => {
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T00:00:00`);
    const difference = Math.round((end - start) / 86400000) + 1;
    return difference > 0 ? difference : 0;
  }, [endDate, startDate]);

  const applyQuickRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setStartDate(start.toISOString().split("T")[0]);
    setEndDate(end.toISOString().split("T")[0]);
  };

  const handleExport = async () => {
    if (!startDate || !endDate) {
      setError("Please select both start and end dates.");
      return;
    }
    if (startDate > endDate) {
      setError("Start date cannot be after end date.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const res = await getRevenueReport(startDate, endDate);
      if (!res || !res.data) {
        setError("Failed to fetch revenue data.");
        setLoading(false);
        return;
      }

      const { data: rows, summary } = res.data;

      const buffer = await buildRevenueWorkbook(rows || [], summary);
      triggerDownload(buffer, `revenue_report_${startDate}_to_${endDate}.xlsx`);

      onClose();
    } catch (err) {
      setError("Export failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-slate-950/70 px-4 py-8 backdrop-blur-md">
      <div className="relative w-full max-w-2xl">
        <div className="overflow-hidden rounded-[28px] border border-white/10 bg-slate-900 shadow-2xl shadow-black/40">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.18),_transparent_30%),radial-gradient(circle_at_bottom_right,_rgba(245,158,11,0.18),_transparent_28%)]" />
          {/* Header */}
          <div className="relative flex items-start justify-between border-b border-white/10 px-6 py-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-emerald-400/20 bg-emerald-500/15">
                <FiBarChart2 className="text-emerald-300" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Admin Revenue Report</h3>
                <p className="mt-1 text-sm text-slate-300">Generate a polished Excel workbook with summary metrics and a daily revenue ledger.</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-xl p-2 text-slate-400 transition-colors hover:bg-white/5 hover:text-white"
            >
              <FiX size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="relative grid gap-6 px-6 py-6 lg:grid-cols-[1.15fr_0.85fr]">
            <div className="space-y-5">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <p className="mb-4 text-xs font-semibold uppercase tracking-[0.24em] text-emerald-300">Report Window</p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-slate-200">
                      <FiCalendar size={14} className="text-slate-400" />
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full rounded-xl border border-slate-700 bg-black px-3 py-3 text-sm text-white outline-none transition [color-scheme:dark] focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-slate-200">
                      <FiCalendar size={14} className="text-slate-400" />
                      End Date
                    </label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full rounded-xl border border-slate-700 bg-black px-3 py-3 text-sm text-white outline-none transition [color-scheme:dark] focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400"
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.24em] text-amber-300">Quick Ranges</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: "Today", days: 0 },
                    { label: "Last 7 Days", days: 7 },
                    { label: "Last 30 Days", days: 30 },
                    { label: "Last 90 Days", days: 90 },
                  ].map(({ label, days }) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => applyQuickRange(days)}
                      className="rounded-full border border-white/10 bg-slate-800 px-3 py-1.5 text-xs font-semibold text-slate-200 transition hover:border-amber-300/40 hover:bg-amber-400/10 hover:text-white"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {error && (
                <p className="rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                  {error}
                </p>
              )}
            </div>

            <div className="space-y-4 rounded-2xl border border-white/10 bg-slate-950/70 p-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-sky-300">Workbook Preview</p>
                <h4 className="mt-2 text-lg font-semibold text-white">Two polished Excel sheets</h4>
                <p className="mt-1 text-sm text-slate-400">The export includes an executive summary and a formatted day-by-day ledger ready for admin review.</p>
              </div>

              <div className="grid gap-3">
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                  <div className="flex items-center gap-3">
                    <div className="rounded-xl bg-emerald-500/15 p-2 text-emerald-300">
                      <FiCalendar />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Range</p>
                      <p className="text-sm font-semibold text-white">{formatDisplayDate(startDate)} - {formatDisplayDate(endDate)}</p>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                  <div className="flex items-center gap-3">
                    <div className="rounded-xl bg-amber-500/15 p-2 text-amber-300">
                      <FiHash />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Coverage</p>
                      <p className="text-sm font-semibold text-white">{reportDays} day{reportDays === 1 ? "" : "s"} selected</p>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                  <div className="flex items-center gap-3">
                    <div className="rounded-xl bg-blue-500/15 p-2 text-blue-300">
                      <FiTrendingUp />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Includes</p>
                      <p className="text-sm font-semibold text-white">Summary metrics, best day, totals, and daily revenue breakdown</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="relative flex justify-end gap-3 border-t border-white/10 px-6 py-5">
            <button
              onClick={onClose}
              className="rounded-xl border border-white/10 bg-slate-800 px-4 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-slate-700 hover:text-white"
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              disabled={loading}
              className="flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-950/40 transition-colors hover:bg-emerald-500 disabled:cursor-not-allowed disabled:bg-emerald-900"
            >
              {loading ? <Loading /> : <><FiDownload size={14} /> Export to Excel</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
